/**
 * siteWash: the site-tinted wash (decided 23 Oct 2026), as a JS helper.
 *
 * The design (research/v3/make/washes.py, site() and site_card()) writes it
 * with CSS relative colour, oklch(from var(--site) L C h), which the build's
 * CSS minifier rejects. This helper does the same maths in JS: it takes the
 * site's colour, keeps its hue, fixes lightness and chroma (so coral, green,
 * navy or yellow all land in the same soft family), adds a second blob 22°
 * along the wheel and a touch of Publish peach, and returns a CSS background
 * (grain first) with plain rgba() stops.
 *
 *   siteWash('#FF8A5E')                  page wash, light
 *   siteWash('#FF8A5E', { dark: true })  a faint glow from the top edge
 *   siteWash('#FF8A5E', { card: true })  the card version (two corner blobs)
 *
 * The site's colour is not stored yet (reading it from the published
 * screenshot is product work), so callers fall back to the Publish wash when
 * they have no colour: siteWash(null) returns null.
 */

const EASE = [1, 0.947, 0.809, 0.619, 0.41, 0.216, 0.069, 0];
const STOPS = [0, 14, 28, 42, 56, 70, 84, 100];

function hexToRgb(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

const toLinear = (c) => {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
};
const toGamma = (v) => {
  const c = v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055;
  return Math.round(Math.min(1, Math.max(0, c)) * 255);
};

/** sRGB hex -> { l, c, h } in OKLCH (h in degrees). */
export function hexToOklch(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb.map(toLinear);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  const C = Math.sqrt(A * A + B * B);
  let H = (Math.atan2(B, A) * 180) / Math.PI;
  if (H < 0) H += 360;
  return { l: L, c: C, h: H };
}

/** OKLCH -> [r, g, b] (0-255), clipped into sRGB. */
export function oklchToRgb(L, C, H) {
  const hr = (H * Math.PI) / 180;
  const a = C * Math.cos(hr);
  const b = C * Math.sin(hr);
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    toGamma(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    toGamma(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    toGamma(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
  ];
}

const clamp = (lo, v, hi) => Math.min(hi, Math.max(lo, v));

function blob(rgb, alpha, size, at) {
  const [r, g, b] = rgb;
  const stops = EASE.map((e, i) => `rgba(${r},${g},${b},${(alpha * e).toFixed(3)}) ${STOPS[i]}%`).join(',');
  return `radial-gradient(${size} at ${at},${stops})`;
}

const PEACH = [255, 227, 210];
const WHITE = [255, 255, 255];

/**
 * @param {string|string[]|null} hex  the site's colour (6-digit hex), or its
 *   previewColors (2 to 3 hex values captured from the snapshot)
 * @param {{ dark?: boolean, card?: boolean }} opts
 * @returns {string|null} a CSS background value, or null without a colour
 */
export default function siteWash(hex, { dark = false, card = false } = {}) {
  // One colour, or the 2 to 3 colours captured from a snapshot (previewColors):
  // the first sets the main blob, the second (when given) the second blob
  // instead of the first hue 22 degrees along, the third replaces the peach.
  const list = (Array.isArray(hex) ? hex : [hex]).map(hexToOklch).filter(Boolean);
  const o = list[0];
  if (!o) return null;
  const { c, h } = o;
  const h2 = list[1] ? list[1].h : (h - 22 + 360) % 360;
  const c2 = list[1] ? list[1].c : c;
  const accent = list[2] ? oklchToRgb(0.92, clamp(0.03, list[2].c * 0.4, 0.06), list[2].h) : null;
  const col = (L, C, H) => oklchToRgb(L, C, H);
  const base = (L, C) => {
    const [r, g, b] = col(L, C, h);
    return `rgb(${r},${g},${b})`;
  };
  let layers;
  if (card && dark) {
    layers = [blob(col(0.64, clamp(0.07, c * 0.7, 0.13), h), 0.16, '90% 120%', '0% 0%'), base(0.2, 0.01)];
  } else if (card) {
    layers = [
      blob(col(0.93, clamp(0.04, c * 0.45, 0.075), h), 1, '80% 120%', '0% 0%'),
      blob(col(0.95, clamp(0.03, c2 * 0.35, 0.06), h2), 0.9, '70% 120%', '100% 100%'),
      base(0.99, 0.008),
    ];
  } else if (dark) {
    layers = [
      blob(col(0.66, clamp(0.08, c * 0.8, 0.15), h), 0.2, '90% 70%', '50% -18%'),
      blob(col(0.6, clamp(0.06, c2 * 0.6, 0.12), h2), 0.1, '60% 80%', '100% 110%'),
      base(0.17, 0.012),
    ];
  } else {
    layers = [
      blob(col(0.9, clamp(0.05, c * 0.5, 0.085), h), 1, '72% 92%', '12% 4%'),
      blob(col(0.93, clamp(0.035, c2 * 0.4, 0.07), h2), 0.9, '60% 86%', '96% 96%'),
      blob(accent || PEACH, 0.5, '50% 60%', '70% -10%'),
      blob(WHITE, 0.65, '46% 58%', '50% 56%'),
      base(0.985, 0.01),
    ];
  }
  return card ? layers.join(',') : `var(--aero-grain),${layers.join(',')}`;
}
